/**
 * PatientListRow Component
 *
 * A single row in the patient list with modern styling
 */

import React, { useMemo } from 'react';
import { Avatar } from '@components/primitives/Avatar';
import {
  ChevronRight as ChevronRightIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { Patient } from '../../../types/types';

export interface PatientListRowProps {
  patient: Patient;
  onSelect: (patientId: string) => void;
}

/**
 * Format date of birth for display as yyyy-mm-dd
 */
function formatDOB(dob: string | null | undefined): string | null {
  if (!dob) return null;
  try {
    const date = new Date(dob);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return dob;
  }
}

/**
 * Calculate age from DOB
 */
function calculateAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  try {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
}

/**
 * Format last encounter date
 */
function formatLastEncounter(patient: Patient): string | null {
  const encounter = patient.latest_encounter;
  if (!encounter) return null;

  const encounterWithScheduled = encounter as typeof encounter & { scheduled_for?: string | null };
  const dateStr = encounterWithScheduled.scheduled_for || encounter.start_time || encounter.created_at;
  if (!dateStr) return null;

  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

export const PatientListRow: React.FC<PatientListRowProps> = React.memo(({ patient, onSelect }) => {
  const theme = useTheme();

  const fullName = useMemo(() => {
    const parts = [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean);
    return parts.join(' ') || 'Unknown Patient';
  }, [patient.first_name, patient.middle_name, patient.last_name]);

  const age = useMemo(() => calculateAge(patient.dob), [patient.dob]);
  const dob = useMemo(() => formatDOB(patient.dob), [patient.dob]);
  const lastEncounter = useMemo(() => formatLastEncounter(patient), [patient]);

  const hasPhone = Boolean(patient.phone || patient.mobile_phone);
  const hasEmail = Boolean(patient.email);

  return (
    <Box
      onClick={() => onSelect(patient.patient_id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 2,
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        id={patient.patient_id}
        firstName={patient.first_name}
        lastName={patient.last_name}
        sx={{
          width: 44,
          height: 44,
          fontSize: '1rem',
          fontWeight: 600,
          flexShrink: 0,
        }}
      />

      {/* Main Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName}
          </Typography>

          {patient.identifier && (
            <Chip
              label={patient.identifier}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 500,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {(age !== null || dob) && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {age !== null && `${age}y`}
              {age !== null && dob && ' · '}
              {dob}
            </Typography>
          )}

          {/* Contact indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasPhone && (
              <PhoneIcon sx={{ fontSize: 14, color: theme.palette.grey[400] }} />
            )}
            {hasEmail && (
              <EmailIcon sx={{ fontSize: 14, color: theme.palette.grey[400] }} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Last Encounter */}
      {lastEncounter && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.8125rem',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {lastEncounter}
        </Typography>
      )}

      {/* Arrow */}
      <IconButton
        size="small"
        sx={{
          color: theme.palette.grey[400],
          flexShrink: 0,
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
});

PatientListRow.displayName = 'PatientListRow';
